import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import JobSearch from "@sourceflow-uk/job-search";
import { motion } from "framer-motion";
import Link from "next/link";
import { useEffect } from "react";
import ReactPlayer from "react-player";
import AnchorLink from "react-anchor-link-smooth-scroll";
import { Col, Container, Row } from "reactstrap";
import styles from "./styles.module.scss";
import SimpleForm from "/components/SimpleForm";
import Arrow from "/public/site-assets/svg/arrow.svg";
import BannerOverlay from "/public/site-assets/svg/banner-overlay.svg";
import BreadCrumb from "../../components/Shared/BreadCrumb";

export const MainBanner = ({
  title,
  content,
  image,
  video,
  breadcrumb_title,
  secondary_breadcrumb_url,
  secondary_breadcrumb_title,
  buttons = [],
  buttonLayout = "horizontal",
  jobSearch,
  displayImage1,
  pageAnchor,
  pageAnchorId,
  className,
  locale,
  backgroundColor = "black",
  textColor = "white",
}) => {
  useEffect(() => {
    Fancybox.bind("[data-fancybox]", {
      // Your custom options
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  const variants_1 = {
    hidden: { opacity: 0.005, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.05 } },
  };
  const variants_2 = {
    hidden: { opacity: 0.005, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } },
  };

  // --- CORRECTED LOGIC ---
  // This creates two distinct sets of classes to prevent any conflicts between them.
  const buttonContainerClasses =
    buttonLayout === "vertical"
      ? "d-flex flex-column align-items-start gap-3 mt-4" // Classes for VERTICAL layout
      : "d-flex align-items-center flex-wrap gap-3 mt-4"; // Classes for HORIZONTAL layout

  return (
    <>
      <section
        className={`${styles.root} ${className} position-relative ${
          displayImage1 ? "" : "hidden-image-1"
        } banner-bg-${backgroundColor} banner-text-${textColor}`}
      >
        <Container>
         
          <Row className="m-0">
            <Col
              lg={6}
              className="contentWrapper d-flex flex-column align-items-center position-relative"
            >
              {breadcrumb_title && (
                <div className={`position-relative wrapper mb-4 w-100`}>
                  <BreadCrumb
                    title={
                      <>
                        {secondary_breadcrumb_url && (
                          <span>
                            <Link href={secondary_breadcrumb_url}>
                              <a>{secondary_breadcrumb_title}</a>
                            </Link>
                          </span>
                        )}
                        <span className="active">{breadcrumb_title}</span>
                      </>
                    }
                    className={`py-2`}
                  />
                </div>
              )}
              <div>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={variants_1}
                >
                  <h1>{title}</h1>
                  {content &&
                    <div dangerouslySetInnerHTML={{ __html: content }} />
                  }

                  <div className={buttonContainerClasses}>
                    {buttons &&
                      buttons.length > 0 &&
                      buttons.map((button, index) => {
                        const buttonClass =
                          styles[button.variant] || styles.primaryBtn;
                        const isSecondaryLink =
                          button.variant === "secondaryLink" ||
                          button.variant === "secondaryLinkWhite";

                        const finalButtonClass = `${buttonClass} ${
                          !isSecondaryLink ? styles.noArrow : ""
                        }`;

                        if (button.popUpModal) {
                          return (
                            <button
                              key={`button-${index}`}
                              className={finalButtonClass}
                              data-fancybox
                              data-src={`#dialog-content-${index}`}
                            >
                              {button.label}
                            </button>
                          );
                        }

                        return (
                          <Link
                            href={button.href || "#"}
                            key={`button-${index}`}
                          >
                            <a
                              className={finalButtonClass}
                              target={button.newTab ? "_blank" : "_self"}
                              rel={
                                button.newTab
                                  ? "noopener noreferrer"
                                  : undefined
                              }
                            >
                              {button.label}
                            </a>
                          </Link>
                        );
                      })}
                  </div>
                </motion.div>

                {jobSearch && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={variants_2}
                  >
                    <JobSearch
                      options={{
                        searchResults: false,
                        searchFilters: false,
                        submitDestination: `${locale ? locale : ""}/jobs`,
                      }}
                      translations={{
                        "input.placeHolder": "Job Title or Keyword",
                      }}
                    />
                  </motion.div>
                )}
              </div>
            </Col>

            {(displayImage1 || video) && (
              <Col lg={6} className="position-relative mainImageWrapper">
                {video && video?.url ? (
                  <div className="rounded-4 overflow-hidden ratio ratio-16x9 my-md-5">
                    <ReactPlayer
                      className="react-player"
                      url={video?.url}
                      controls={video?.controls || false}
                      playing={video?.autoplay || false}
                      muted={video?.autoplay || false}
                      playsinline={true}
                      width="100%"
                      height="100%"
                    />
                  </div>
                ) : displayImage1 && image?.url ? (
                  <img
                    src={image?.url}
                    alt={image?.alt}
                    className="card-img rounded-4"
                    loading={"eager"}
                  />
                ) : null}
              </Col>
            )}

            <div className="imageWrapper position-absolute p-0">
              <BannerOverlay />
            </div>
          </Row>

          {pageAnchor && (
            <AnchorLink
              offset="80"
              href={`#${pageAnchorId}`}
              className="banner-anchor position-absolute"
              aria-label="Scroll Bottom"
            >
              <Arrow />
            </AnchorLink>
          )}
        </Container>
      </section>

      <div style={{ display: "none" }}>
        {buttons &&
          buttons.length > 0 &&
          buttons
            .filter((button) => button.popUpModal)
            .map((button, index) => (
              <div
                key={`modal-${index}`}
                id={`dialog-content-${index}`}
                className="dialog-content"
              >
                {button.dialog_content && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: button.dialog_content,
                    }}
                  />
                )}
                <hr />
                {button.popUpModal_form_id && (
                  <SimpleForm
                    formId={button.popUpModal_form_id}
                    successPageUrl={button.successPageUrl}
                  />
                )}
              </div>
            ))}
      </div>
    </>
  );
};

export default MainBanner;
